import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppHeader } from '../../components/Layout/AppHeader';
import { getDetailsByCargo, deleteDetail, deleteImage, getImages, sendVgmByDetail, editCargoDetailVgmReference, editDetail, getPositionsByDate, sendCargoReportByEmail, getDeliveryNotesByCargo } from '../../services/api';
import { getQueuedDeliveryNotes } from '../../services/db';
import { DELIVERY_NOTES_CHANGED_EVENT } from '../../services/deliveryNotes';
import { DeliveryNoteCardActions } from '../../components/DeliveryNoteSection/DeliveryNoteCardActions';
import type { CargoReportRequestResponse } from '../../services/api';
import { telemetryLog } from '../../services/telemetry';
import { getEffectivePreferences, type EffectivePreferences } from '../../services/features';
import { isSelfManagedCargo, displayProductNames, productDraftOf } from '../../services/selfManaged';
import { DraftProduct } from '../../components/DraftProduct/DraftProduct';
import { plateStyle } from '../../services/licensePlate';
import { resolveCountryIso } from '../../services/countries';
import { LicensePlate } from '../../components/LicensePlate/LicensePlate';
import { useFeatures } from '../../hooks/useFeatures';
import { useChangeFeed } from '../../hooks/useChangeFeed';
import { getSupplierStamp } from '../../utils/supplierStamp';
import { useAuth } from '../../context/AuthContext';
import { cacheCargoDetails, getCachedCargoDetails, getPendingDetailsByCargo, addPendingDetail, updatePendingDetailFields, deletePendingDetail, getCachedPositions, findCachedPositionByCargo, updateCachedCargoDetails } from '../../services/db';
import { invalidateSwApiCache } from '../../services/swCache';
import type { PendingDetail } from '../../services/db';
import { notifyPendingChanged, triggerOfflineFlush, usePendingCount } from '../../hooks/useOfflineSync';
import { isOfflineForUser } from '../../services/networkStatus';
import { track } from '../../services/analytics';
import { usePullToRefresh, PullIndicator } from '../../hooks/usePullToRefresh';
import { captureError } from '../../services/errorTracking';
import { PageSpinner } from '../../components/PageSpinner/PageSpinner';
import type { DeliveryNote, Detail, Position } from '../../types';
import './DetailPage.css';

// ── Toast ──────────────────────────────────────────────────────────────────
function Toast({ msg, onDone }: { msg: string; onDone: () => void }) {
  useEffect(() => {
    // 2,5 s para los cortos; los largos (el del informe lleva el correo del
    // usuario) se quedan hasta 6 s, que es lo que cuesta leerlos.
    const t = setTimeout(onDone, Math.min(6000, 2500 + Math.max(0, msg.length - 40) * 45));
    return () => clearTimeout(t);
  }, [onDone, msg]);
  return <div className="toast">{msg}</div>;
}

// ── Placa estilo contenedor de carga ──────────────────────────────────────
// Replica el aspecto de la matrícula impresa en un contenedor real:
//   ┌──────┬───────────────┬───┐
//   │ MRKU │     607042    │ 0 │
//   └──────┴───────────────┴───┘
// El número canónico ISO 6346 es 4 letras + 6 dígitos + 1 dígito de control.
// Si el valor no encaja con ese patrón, lo mostramos completo en el centro
// para que la card no se rompa con datos atípicos.
function ContainerPlate({ value }: { value: string }) {
  const { t } = useTranslation();
  const cleaned = value.replace(/\s/g, '').toUpperCase();
  const m = cleaned.match(/^([A-Z]{4})(\d{6})(\d)$/);
  if (m) {
    return (
      <span className="container-plate" aria-label={t('detail.plateContainer', { value: cleaned })}>
        <span className="container-plate__prefix">{m[1]}</span>
        <span className="container-plate__number">{m[2]}</span>
        <span className="container-plate__check">{m[3]}</span>
      </span>
    );
  }
  return (
    <span className="container-plate" aria-label={t('detail.plateContainer', { value: cleaned })}>
      <span className="container-plate__number">{cleaned}</span>
    </span>
  );
}

// ── Precinto de seguridad estilo bolt seal ────────────────────────────────
// Recuerda al precinto físico amarillo + perno metálico que se pone en el
// contenedor: cabeza tipo "bandera" alargada, cuerpo cilíndrico con el
// número grabado y un pequeño perno plateado a la derecha.
function SealBadge({ value }: { value: string }) {
  const { t } = useTranslation();
  return (
    <span className="seal" aria-label={t('detail.plateSeal', { value })}>
      <span className="seal__body">
        <span className="seal__flag" />
        <span className="seal__number">{value}</span>
      </span>
      <span className="seal__bolt" />
    </span>
  );
}

// ── Icono estado sincronización ────────────────────────────────────────────
function SyncIcon({ isOffline }: { isOffline?: boolean }) {
  const { t } = useTranslation();
  if (isOffline) {
    return (
      <svg viewBox="0 0 24 24" fill="#A05A00" width="18" height="18" aria-label={t('detail.syncPending')}>
        <path d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/>
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="#187236" width="18" height="18" aria-label={t('detail.synced')}>
      <path d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"/>
    </svg>
  );
}

// ── Detail card con edición inline ────────────────────────────────────────
type DeliveryNoteBadge = 'done' | 'pending' | 'error';
interface DeliveryNoteInfo { status: DeliveryNoteBadge; note?: DeliveryNote }

function DetailCard({
  detail,
  order,
  position,
  dateStr,
  prefs,
  savingId,
  hidden,
  onSetHidden,
  onSave,
  onDelete,
  onVgm,
  canVgm,
  canPlate,
  selfManaged,
  showSupplierStamp,
  deliveryNote,
  canManageDeliveryNote,
}: {
  detail: Detail;
  order: number;
  position?: Position;
  dateStr: string;
  prefs: EffectivePreferences;
  savingId: number | null;
  hidden: boolean;
  onSetHidden: (value: boolean) => void;
  onSave: (id: number, fields: { netWeight: number; tare: number; packageNumber: number }) => void;
  onDelete: (d: Detail) => void;
  onVgm: (d: Detail) => void;
  canVgm: boolean;
  canPlate: boolean;
  selfManaged: boolean;
  showSupplierStamp: boolean;
  /** El documento de transporte (DDT) del contenedor, si lo hay. */
  deliveryNote?: DeliveryNoteInfo;
  /** Quien lo gestiona (proveedor y admin) ve su recuadro de opciones. */
  canManageDeliveryNote: boolean;
}) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const supplierStamp = showSupplierStamp ? getSupplierStamp(detail, i18n.language) : null;
  // El estado hidden lo gestiona DetailPage (para que los botones globales
  // de "expandir/colapsar todos" puedan cambiarlo sobre todos los items).
  // La persistencia en localStorage por idCargoDetail también vive arriba.
  const setHidden = (val: boolean) => onSetHidden(val);
  const [netWeight, setNetWeight] = useState(detail.netWeight === 0 ? '' : String(detail.netWeight));
  const [tare, setTare] = useState(detail.tare === 0 ? '' : String(detail.tare));
  const [packageNumber, setPackageNumber] = useState(detail.packageNumber === 0 ? '' : String(detail.packageNumber));

  const isSaving = savingId === detail.idCargoDetail;
  // Item NEW pendiente: aún no tiene id de servidor (lo identificamos con
  // idCargoDetail<=0). No podemos editarlo/verlo/enviar VGM hasta que el
  // sync lo cree y obtenga el id real.
  const isPendingNew = detail.idCargoDetail <= 0;

  const handleSave = () => {
    if (isSaving) return;
    onSave(detail.idCargoDetail, {
      netWeight: parseFloat(netWeight) || 0,
      tare: parseFloat(tare) || 0,
      packageNumber: parseInt(packageNumber) || 0,
    });
  };

  // Estilo de la matrícula: país del formato del número y, si no basta, el de
  // la carga (ver services/licensePlate). Se calcula al pintar: vale para las
  // cargas de siempre y las nuevas.
  const loadCountry = resolveCountryIso(position?.countryCode, position?.countryName);
  const truckPlateStyle = useMemo(
    () => plateStyle(detail.numberPlat, loadCountry),
    [detail.numberPlat, loadCountry],
  );
  const trailerPlate = detail.trailerNumberPlat?.trim();
  const trailerPlateStyle = useMemo(
    () => plateStyle(trailerPlate, loadCountry),
    [trailerPlate, loadCountry],
  );

  return (
    <div className={`detail-card ${detail.isOffline ? 'detail-card--offline' : ''}`}>
      {/* Cabecera */}
      <div className="detail-card__header">
        <div className="detail-card__header-left">
          <SyncIcon isOffline={detail.isOffline} />
          <div className="detail-card__title-wrap">
            {/* En self-managed no hay transportista (saldría un "—" suelto):
                se muestra el contenedor y el producto, que es lo que identifica
                el registro de un vistazo. El clásico mantiene el transportista. */}
            <span className="detail-card__pos">
              ({order}){' '}
              {selfManaged
                ? ([detail.containerNumber, position ? (displayProductNames(position) ?? productDraftOf(position)) : undefined]
                    .filter((v) => v && String(v).trim())
                    .join(' · ') || '—')
                : (detail.carrierName || '—')}
            </span>
            <div className="detail-card__plates">
              {/* La matrícula no aplica al perfil self-managed: sin el campo,
                  saldría siempre "—". Si no se sabe de qué país es, la placa
                  se pinta genérica antes que atribuirle un país que no es. */}
              {canPlate && <LicensePlate number={detail.numberPlat} style={truckPlateStyle} vehicle="truck" />}
              {/* Remolque: solo si lo hay (muchos detalles no lo rellenan), con
                  el estilo de su país o genérica si no se reconoce. */}
              {canPlate && trailerPlate && <LicensePlate number={trailerPlate} style={trailerPlateStyle} vehicle="trailer" />}
              {detail.containerNumber && <ContainerPlate value={detail.containerNumber} />}
            </div>
            {detail.sealNumber && <SealBadge value={detail.sealNumber} />}
            {deliveryNote && (
              <span className={`ddt-badge ddt-badge--${deliveryNote.status}`} title={t(`deliveryNote.badgeTitle.${deliveryNote.status}`)}>
                {t(`deliveryNote.badge.${deliveryNote.status}`)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Grupo edición inline — siempre visible (salvo hidden) */}
      {!hidden && (
        <div className="detail-card__edit-group">
          <div className="detail-card__fields">
            <div className="inline-field">
              <label className="inline-field__label">{t('detail.card.netWeight')}</label>
              <input
                className="inline-field__input"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={netWeight}
                onChange={(e) => setNetWeight(e.target.value)}
                placeholder="0"
                disabled={isSaving}
              />
            </div>
            <div className="inline-field">
              <label className="inline-field__label">{t('detail.card.tare')}</label>
              <input
                className="inline-field__input"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={tare}
                onChange={(e) => setTare(e.target.value)}
                placeholder="0"
                disabled={isSaving}
              />
            </div>
            <div className="inline-field">
              <label className="inline-field__label">{t('detail.card.bundles')}</label>
              <input
                className="inline-field__input"
                type="number"
                inputMode="numeric"
                step="1"
                min="0"
                value={packageNumber}
                onChange={(e) => setPackageNumber(e.target.value)}
                placeholder="0"
                disabled={isSaving}
              />
            </div>
          </div>

          {/* Botón guardar */}
          <button
            className="save-btn"
            onClick={handleSave}
            disabled={isSaving}
            aria-label={t('detail.card.save')}
            style={{ opacity: isSaving ? 0.4 : 1 }}
          >
            {isSaving
              ? <div className="spinner spinner--small spinner--white" />
              : (
                <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
                  <path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/>
                </svg>
              )
            }
          </button>
        </div>
      )}

      {/* Datos de solo lectura */}
      {!hidden && (
        <div className="detail-card__readonly">
          {detail.humidity != null && (
            <span className="readonly-chip">{t('detail.card.humidity', { value: detail.humidity })}</span>
          )}
          {detail.containerNumber && (
            <span className="readonly-chip">{t('detail.card.cont', { value: detail.containerNumber })}</span>
          )}
          {detail.checkIn && (
            <span className="readonly-chip">{t('detail.card.checkIn', { value: detail.checkIn })}</span>
          )}
          {detail.checkOut && (
            <span className="readonly-chip">{t('detail.card.lastUpdate', { value: detail.checkOut })}</span>
          )}
          {supplierStamp && (
            <span className="readonly-chip readonly-chip--supplier" title={supplierStamp.who}>
              {t('detail.card.supplierFilled', { who: supplierStamp.who, when: supplierStamp.when })}
            </span>
          )}
          {detail.comments && (
            <span className="readonly-chip">{t('detail.card.exitTime', { value: detail.comments })}</span>
          )}
        </div>
      )}

      {/* Separador + botones de acción */}
      <div className="detail-card__divider" />
      <div className="detail-card__actions">
        <button
          className="action-btn action-btn--ghost"
          onClick={() => setHidden(!hidden)}
        >
          {hidden ? t('detail.card.show') : t('detail.card.hide')}
        </button>
        <button
          className="action-btn action-btn--view"
          onClick={() => navigate(`/view-detail/${detail.idCargoDetail}`, { state: { detail, position } })}
        >
          {t('detail.card.view')}
        </button>
        <button
          className="action-btn action-btn--edit"
          onClick={() => navigate(`/edit-detail/${detail.idCargo}/${detail.idCargoDetail}`, {
            state: { detail, position, date: dateStr }
          })}
        >
          {t('detail.card.edit')}
        </button>
        {canVgm && (
          <button
            className="action-btn action-btn--vgm"
            onClick={() => onVgm(detail)}
            disabled={isPendingNew}
            title={isPendingNew ? t('detail.card.availableOnSync') : undefined}
          >
            VGM
          </button>
        )}
        {prefs.deleteEnabled && (
          <button className="action-btn action-btn--delete" onClick={() => onDelete(detail)}>
            {t('detail.card.delete')}
          </button>
        )}
        {/* Distintivo "VGM enviado". Gated igual que el botón: para quien no
            tiene VGM no debe aparecer NADA del trámite, ni siquiera el estado
            de una carga que lo tuviera de otro flujo. */}
        {canVgm && detail.vgmConfirmation && (
          <span className="action-vgm-flag" title={detail.vgmConfirmation}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12" aria-hidden="true">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
            {t('detail.card.vgmOk')}
          </span>
        )}
      </div>
      {/* Opciones del documento de transporte (DDT), aparte del resto: solo
          para quien lo gestiona y con el contenedor ya en el servidor. */}
      {canManageDeliveryNote && !isPendingNew && (
        <DeliveryNoteCardActions
          idCargoDetail={detail.idCargoDetail}
          idCargo={detail.idCargo}
          note={deliveryNote?.note}
          status={deliveryNote?.status}
        />
      )}
    </div>
  );
}

// ── Página principal ───────────────────────────────────────────────────────
export function DetailPage() {
  const { idCargo } = useParams<{ idCargo: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Fallback a query param `?date=YYYY-MM-DD` para entrar al detalle desde
  // un deep link (ej: notificación push), donde no hay location.state.
  const dateStr: string = (() => {
    if (location.state?.date) return location.state.date as string;
    const q = new URLSearchParams(location.search).get('date') ?? '';
    return /^\d{4}-\d{2}-\d{2}$/.test(q) ? q : '';
  })();

  // Position normalmente viene del state (navegación desde la lista). Si
  // entramos sin ella (deep link de push, o justo tras crear la planning,
  // que navega directo a /detail sin la Position), la recuperamos por
  // (idCargo, dateStr): primero del caché local y, si no está, con un fetch
  // fresco al backend. Esto es CRÍTICO para crear el primer detalle: sin la
  // Position, EditDetailPage no puede arrastrar los IDs maestros
  // (idProduct/idPlanning/idCarrier) que el backend exige por FK. Si aun así
  // no se consigue, la página degrada (sin bloque de info, sin VGM) pero los
  // details cargan.
  const [position, setPosition] = useState<Position | undefined>(location.state?.position);
  useEffect(() => {
    if (position || !idCargo) return;
    let cancelled = false;
    (async () => {
      // 0. Sin fecha (app relanzada sobre /detail/<id>, sin history.state ni
      //    ?date=): la unica pista es el idCargo; se busca en TODAS las fechas
      //    cacheadas. Era el hueco por el que "+" creaba detalles sin ids
      //    maestros (FK_CargoDetail_Product en el servidor).
      if (!dateStr) {
        const anyDate = await findCachedPositionByCargo(Number(idCargo)).catch(() => null);
        if (!cancelled && anyDate) setPosition(anyDate);
        return;
      }
      // 1. Intento rápido desde el caché local de positions por fecha.
      const cached = await getCachedPositions(dateStr).catch(() => null);
      if (cancelled) return;
      const cachedMatch = cached?.find((p) => p.idCargo === Number(idCargo));
      if (cachedMatch) { setPosition(cachedMatch); return; }
      // 2. Sin match en caché (típico: planning recién creada, aún no
      //    cacheada) → traemos fresco del backend por fecha y buscamos la
      //    cargo. Sin red no hay nada que traer; degradamos.
      if (isOfflineForUser()) return;
      const [y, m, d] = dateStr.split('-').map(Number);
      if (!y || !m || !d) return;
      try {
        const res = await getPositionsByDate(y, m, d);
        if (cancelled) return;
        const data = (res.data ?? []) as Position[];
        const match = data.find((p) => p.idCargo === Number(idCargo));
        if (match) setPosition(match);
      } catch (e) {
        captureError(e, { where: 'DetailPage.recoverPosition', idCargo, dateStr });
      }
    })();
    return () => { cancelled = true; };
  }, [position, idCargo, dateStr]);

  const { claims } = useAuth();
  // Preferencias ya recortadas por el perfil: para self-managed, VGM y borrado
  // llegan aquí en false aunque quedara un valor antiguo en localStorage.
  const prefs = getEffectivePreferences(claims);
  const features = useFeatures();
  // El switch "Show All" (mostrar también las cargas ya cargadas) solo se ofrece
  // a usuarios de España (ES en sus países). Para el resto NO se muestra el
  // switch y la vista es SIEMPRE "Show All" (showLoaded = true).
  const hasES = claims.userCountries.includes('ES');
  // El VGM (Verified Gross Mass) es un trámite de los puertos españoles (Portic).
  // Solo se ofrece a usuarios de España: sin ES no se muestran ni el botón VGM de
  // cada card ni la nube de "enviar todos los VGM".
  const vgmEnabled = prefs.vgmEnabled && hasES;
  // Cómo se pinta una carga depende de LA CARGA, no de quién la mire. Un admin
  // del flujo clásico que abre una carga de Pakistán veía el aspecto clásico:
  // un guion donde debería ir el producto (esas cargas no tienen
  // transportista), y matrícula y VGM ofrecidos sobre datos que no existen.
  const cargoSelfManaged = claims.isSelfManaged || (position ? isSelfManagedCargo(position) : false);
  const [details, setDetails] = useState<Detail[]>([]);
  const [loading, setLoading] = useState(true);
  // IDs de detalles ocultos (cards colapsadas). Centralizado aquí para que
  // los botones de "expandir/colapsar todos" en la toolbar puedan operar
  // sobre todas las cards a la vez. Persistencia por id en localStorage —
  // mismo formato que tenía la versión anterior, así no perdemos los
  // estados que el usuario ya había guardado.
  const [hiddenIds, setHiddenIds] = useState<Set<number>>(new Set());
  const [showLoaded, setShowLoaded] = useState(() =>
    // Sin ES: no hay switch → siempre "Show All".
    !hasES
      ? true
      : localStorage.getItem('DETAIL_SHOW_LOADED') !== null
        ? localStorage.getItem('DETAIL_SHOW_LOADED') === 'true'
        : !prefs.vgmEnabled
  );
  const [savingId, setSavingId] = useState<number | null>(null);
  const [toast, setToast] = useState('');
  // Confirmación de eliminar con input para evitar clicks accidentales.
  const [deleteTarget, setDeleteTarget] = useState<Detail | null>(null);
  const [deleteInput, setDeleteInput] = useState('');
  const deleteInputRef = useRef<HTMLInputElement | null>(null);
  const [deletingNow, setDeletingNow] = useState(false);
  const [sortAscending, setSortAscending] = useState(
    () => localStorage.getItem('DETAIL_SORT_ASC') !== 'false'
  );
  const [sendingVgm, setSendingVgm] = useState(false);
  const [vgmConfirmOpen, setVgmConfirmOpen] = useState(false);
  // Inicializar buscador desde sessionStorage por idCargo, para que la
  // búsqueda sobreviva a la navegación al detalle de un contenedor
  // (/view-detail, /edit-detail) y la vuelta atrás. Claves por idCargo
  // para que cada carga conserve su filtro de forma independiente.
  const searchQueryKey = `DETAIL_SEARCH_QUERY_${idCargo}`;
  const searchOpenKey = `DETAIL_SEARCH_OPEN_${idCargo}`;
  const scrollKey = `DETAIL_SCROLL_${idCargo}`;
  const [searchOpen, setSearchOpen] = useState<boolean>(
    () => sessionStorage.getItem(searchOpenKey) === '1'
  );
  const [searchQuery, setSearchQuery] = useState<string>(
    () => sessionStorage.getItem(searchQueryKey) ?? ''
  );
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Persistir cambios de búsqueda
  useEffect(() => {
    if (searchQuery) sessionStorage.setItem(searchQueryKey, searchQuery);
    else sessionStorage.removeItem(searchQueryKey);
  }, [searchQuery, searchQueryKey]);
  useEffect(() => {
    if (searchOpen) sessionStorage.setItem(searchOpenKey, '1');
    else sessionStorage.removeItem(searchOpenKey);
  }, [searchOpen, searchOpenKey]);

  const scrollRestoredRef = useRef(false);
  // True mientras restauramos scroll de forma programática. Evita que el
  // scroll artificial (los scrollTo de la restauración) se guarde como si
  // fuera scroll del usuario y pise el valor objetivo en sessionStorage.
  const restoringScrollRef = useRef(false);

  // Guardar posición en scroll continuo. El debounce evita escribir en cada
  // píxel, pero si el usuario navega al view-detail/edit-detail antes de que
  // el timer dispare, el valor pendiente se persiste en el cleanup y en
  // pagehide — así no perdemos el último scroll.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let pendingY: number | null = null;
    const flush = () => {
      if (pendingY != null) {
        sessionStorage.setItem(scrollKey, String(pendingY));
        pendingY = null;
      }
    };
    const onScroll = () => {
      if (restoringScrollRef.current) return;
      pendingY = window.scrollY;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { timer = null; flush(); }, 100);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', flush);
    return () => {
      if (timer) clearTimeout(timer);
      flush();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', flush);
    };
  }, [scrollKey]);

  const sortedDetails = useMemo(() => {
    const server = details.filter((d) => d.idCargoDetail > 0);
    const offline = details.filter((d) => d.idCargoDetail <= 0);
    const sorted = [...server].sort((a, b) =>
      sortAscending ? a.idCargoDetail - b.idCargoDetail : b.idCargoDetail - a.idCargoDetail
    );
    return [...sorted, ...offline];
  }, [details, sortAscending]);

  // Filtro de búsqueda. Contains case-insensitive sobre todos los campos
  // visibles en la card y en la vista de detalle. No toca el fetch ni el cache.
  const matchesSearch = useCallback((d: Detail, query: string): boolean => {
    if (!query) return true;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const fields: Array<string | number | undefined> = [
      d.containerNumber,
      d.numberPlat,
      d.trailerNumberPlat,
      d.sealNumber,
      d.sealVeter,
      d.carrierName,
      d.comments,
      d.lotNumber,
      d.location,
      d.checkIn,
      d.checkOut,
      d.vgmConfirmation,
      d.packageNumber,
      d.humidity,
      d.tare,
      d.netWeight,
      d.vgm,
      d.idCargoDetail,
    ];
    return fields.some((v) => {
      if (v == null) return false;
      return String(v).toLowerCase().includes(q);
    });
  }, []);

  const visibleDetails = useMemo(
    () => sortedDetails.filter((d) => matchesSearch(d, searchQuery)),
    [sortedDetails, searchQuery, matchesSearch]
  );

  // Restaurar scroll una vez la lista visible está renderizada. A diferencia
  // de un scrollTo de un solo disparo, reintentamos por frames hasta que el
  // documento sea lo bastante alto para alcanzar el objetivo. Esto es lo que
  // hace que volver de Guardar (que refresca la lista desde red, con el
  // contenido asentándose más tarde) conserve la posición igual que volver de
  // VER (que resuelve al instante desde caché). Si la búsqueda no devuelve
  // resultados, restauramos al tope.
  useEffect(() => {
    if (loading || scrollRestoredRef.current) return;
    const saved = sessionStorage.getItem(scrollKey);
    if (!saved) { scrollRestoredRef.current = true; return; }
    if (visibleDetails.length === 0) {
      scrollRestoredRef.current = true;
      window.scrollTo(0, 0);
      return;
    }
    // Marcamos como restaurado ya para que el efecto no se re-ejecute (y no
    // re-lea sessionStorage) mientras la lista se asienta; el objetivo queda
    // capturado en la variable local.
    scrollRestoredRef.current = true;
    restoringScrollRef.current = true;
    const target = Number(saved);
    let raf = 0;
    let attempts = 0;
    const settle = () => {
      const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      window.scrollTo(0, Math.min(target, maxScroll));
      attempts++;
      // Seguimos reintentando mientras el documento aún no llegue al objetivo
      // (cards/imágenes midiéndose tras el refresh), con un tope (~20 frames)
      // para no bucear indefinidamente si el contenido quedó más corto.
      if (maxScroll < target && attempts < 20) {
        raf = requestAnimationFrame(settle);
      } else {
        restoringScrollRef.current = false;
      }
    };
    raf = requestAnimationFrame(settle);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      restoringScrollRef.current = false;
    };
  }, [loading, visibleDetails.length, scrollKey]);

  const openSearch = () => {
    setSearchOpen(true);
    requestAnimationFrame(() => searchInputRef.current?.focus());
  };
  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
  };
  const toggleSearch = () => {
    if (searchOpen) closeSearch();
    else openSearch();
  };

  // ── Informe por correo ────────────────────────────────────────────────────
  // Manda al servidor la lista que hay EN PANTALLA (búsqueda y "Ver todas"
  // aplicados) y vuelve al instante: el .docx con ficha y fotos se genera en
  // el servidor y llega al correo del propio usuario. Los pendientes de la
  // cola offline aún no tienen id en el servidor, así que no pueden ir.
  const [sendingReport, setSendingReport] = useState(false);
  const sendReport = async () => {
    if (sendingReport || !idCargo) return;
    const ids = visibleDetails.map((d) => d.idCargoDetail).filter((id) => id > 0);
    if (ids.length === 0) { setToast(t('detail.report.nothing')); return; }
    setSendingReport(true);
    try {
      const { data } = await sendCargoReportByEmail(Number(idCargo), ids);
      telemetryLog('informe_solicitado', { idCargo: Number(idCargo), detalles: ids.length, enviosHoy: data.sentToday });
      setToast(t('detail.report.sent', { email: data.email ?? '', sentToday: data.sentToday, limit: data.dailyLimit }));
    } catch (err) {
      const axiosErr = err as { isAxiosError?: boolean; response?: { status?: number; data?: CargoReportRequestResponse } };
      const status = axiosErr?.response?.status;
      if (status === 429) {
        const limit = axiosErr.response?.data?.dailyLimit ?? 3;
        telemetryLog('informe_tope_diario', { idCargo: Number(idCargo), detalles: ids.length, tope: limit });
        setToast(t('detail.report.limit', { limit }));
      } else if (!axiosErr?.isAxiosError || axiosErr.response == null) {
        setToast(t('detail.report.offline'));
      } else {
        setToast(t('detail.report.error'));
      }
    } finally {
      setSendingReport(false);
    }
  };

  // Fusiona los detalles del servidor (o caché) con los pendientes en cola.
  // Replica el comportamiento de la app Android, que muestra los items
  // marcados como NEW/EDIT en la lista junto a los sincronizados, con un
  // icono distinto para que el usuario sepa que están por enviar.
  const mergeWithPending = useCallback(async (serverList: Detail[]): Promise<Detail[]> => {
    if (!idCargo) return serverList;
    let pending: PendingDetail[];
    try {
      pending = await getPendingDetailsByCargo(Number(idCargo));
    } catch (e) {
      captureError(e, { where: 'DetailPage.getPendingDetailsByCargo', idCargo });
      return serverList;
    }
    if (pending.length === 0) return serverList;

    // 1. EDIT pendientes: sobrescriben el detalle del servidor con los
    //    cambios locales y lo marcan como offline para que el usuario lo
    //    distinga visualmente.
    const editsById = new Map<number, PendingDetail>();
    const deletesById = new Set<number>();
    const news: PendingDetail[] = [];
    for (const p of pending) {
      if (p.operateType === 'EDIT' && p.idCargoDetail > 0) editsById.set(p.idCargoDetail, p);
      else if (p.operateType === 'DELETE' && p.idCargoDetail > 0) deletesById.add(p.idCargoDetail);
      else if (p.operateType === 'NEW') news.push(p);
    }

    const merged: Detail[] = [];
    for (const d of serverList) {
      if (deletesById.has(d.idCargoDetail)) continue; // ocultar mientras se confirma DELETE
      const editPending = editsById.get(d.idCargoDetail);
      if (editPending) {
        merged.push({ ...d, ...editPending.detail, isOffline: true });
      } else {
        merged.push(d);
      }
    }
    // 2. NEW pendientes: aún no tienen idCargoDetail. Usamos el negativo del
    //    id de la cola como clave única estable para el render — DetailCard
    //    sabe filtrar idCargoDetail<=0 para no llamar a getImages.
    for (const n of news) {
      merged.push({ ...n.detail, idCargoDetail: -n.id, idCargo: Number(idCargo), isOffline: true } as Detail);
    }
    return merged;
  }, [idCargo]);

  const fetchDetails = useCallback(async () => {
    if (!idCargo) return;
    // Si sabemos que estamos offline (red caída o modo manual), saltamos
    // la llamada al API y leemos directamente de IDB. Sin esto, el SW
    // (NetworkFirst para api-v1) devolvería una respuesta cacheada con
    // 24h de TTL — datos previos a mutaciones locales — y el cliente la
    // trataría como fresca, sobrescribiendo la IDB y reintroduciendo en
    // pantalla items que el usuario ya borró (fantasmas).
    if (isOfflineForUser()) {
      setLoading(true);
      try {
        const cached = await getCachedCargoDetails(Number(idCargo));
        setDetails(await mergeWithPending(cached ?? []));
      } catch (e) {
        // Sin catch salía como promesa no capturada (iOS al volver de
        // segundo plano con IndexedDB caída). La pantalla se queda con lo que
        // ya mostraba.
        captureError(e, { where: 'DetailPage.cachedDetails.offline', idCargo });
      } finally {
        setLoading(false);
      }
      return;
    }
    // Online: caché PRIMERO (stale-while-revalidate). Pintamos lo guardado YA
    // (si hay) y revalidamos por detrás; el spinner solo si no hay nada. Así,
    // en "lie-fi", el detalle aparece al instante en vez de quedarse en blanco
    // esperando el timeout de la petición.
    // Con catch: si IndexedDB falla (iOS al volver de segundo plano) se sigue
    // a la red como si no hubiera caché, en vez de soltar una promesa sin
    // capturar y dejar la pantalla sin pedir los datos.
    const cached = await getCachedCargoDetails(Number(idCargo)).catch((e) => {
      captureError(e, { where: 'DetailPage.cachedDetails.online', idCargo });
      return null;
    });
    const hadCache = !!cached && cached.length > 0;
    if (hadCache) setDetails(await mergeWithPending(cached!));
    else setLoading(true);
    try {
      const res = await getDetailsByCargo(Number(idCargo), showLoaded);
      const data = res.data ?? [];
      // Cachear en IndexedDB para uso offline
      await cacheCargoDetails(Number(idCargo), data);
      setDetails(await mergeWithPending(data));
    } catch (e) {
      captureError(e, { where: 'DetailPage.getDetailsByCargo', idCargo });
      // Ya pintamos la caché arriba si la había; si no, mostramos lo que haya
      // en IDB + pendientes (puede ser solo pendientes locales).
      if (!hadCache) setDetails(await mergeWithPending(cached ?? []));
    } finally {
      setLoading(false);
    }
  }, [idCargo, showLoaded, mergeWithPending]);

  useEffect(() => { fetchDetails(); }, [fetchDetails]);

  // Documento de transporte (DDT) de cada contenedor (2026-10-23): solo para
  // quien lo gestiona (proveedor y admin). Lo del servidor, y por encima lo
  // pendiente en la cola local, que es lo último que se pidió.
  const [deliveryNotes, setDeliveryNotes] = useState<Map<number, DeliveryNoteInfo>>(() => new Map());
  useEffect(() => {
    if (!features.deliveryNote || !idCargo) return;
    let cancelled = false;
    const load = async () => {
      const next = new Map<number, DeliveryNoteInfo>();
      if (!isOfflineForUser()) {
        try {
          const res = await getDeliveryNotesByCargo(Number(idCargo));
          for (const note of res.data ?? []) next.set(note.idCargoDetail, { status: 'done', note });
        } catch { /* sin respuesta: solo lo local */ }
      }
      try {
        for (const item of await getQueuedDeliveryNotes()) {
          if (item.idCargo !== Number(idCargo)) continue;
          if (item.error) next.set(item.idCargoDetail, { status: 'error' });
          else if (item.op === 'upload') next.set(item.idCargoDetail, { status: 'pending' });
          else next.delete(item.idCargoDetail);
        }
      } catch { /* sin IndexedDB */ }
      if (!cancelled) setDeliveryNotes(next);
    };
    void load();
    window.addEventListener(DELIVERY_NOTES_CHANGED_EVENT, load);
    return () => { cancelled = true; window.removeEventListener(DELIVERY_NOTES_CHANGED_EVENT, load); };
  }, [features.deliveryNote, idCargo, details.length]);

  // El latido de cambios (2026-09-10): si otro toca un contenedor de ESTA
  // carga (el proveedor desde su PWA, el controlador desde otro móvil, el
  // portal), la lista se vuelve a leer en silencio. La caché local se pinta
  // primero, así que no hay spinner ni parpadeo.
  useChangeFeed({ cargoIds: idCargo ? [Number(idCargo)] : [] }, () => { void fetchDetails(); });

  // Cuando cambia la lista de detalles, releemos qué cards estaban ocultas
  // según el localStorage. Esto cubre la primera carga y los refrescos del
  // SW (que pueden traer items nuevos cuya preferencia ya estaba guardada).
  useEffect(() => {
    const next = new Set<number>();
    for (const d of details) {
      if (localStorage.getItem(`DETAIL_HIDDEN_${d.idCargoDetail}`) === 'true') {
        next.add(d.idCargoDetail);
      }
    }
    setHiddenIds(next);
  }, [details]);

  // Toggle individual disparado desde una DetailCard. Actualiza state +
  // persiste en localStorage con el mismo formato que antes.
  const setDetailHidden = useCallback((id: number, value: boolean) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (value) next.add(id); else next.delete(id);
      return next;
    });
    if (value) localStorage.setItem(`DETAIL_HIDDEN_${id}`, 'true');
    else localStorage.removeItem(`DETAIL_HIDDEN_${id}`);
  }, []);

  // Acciones globales: aplicar a los detalles VISIBLES (los que pasan el
  // filtro de búsqueda y el toggle "Ver todas"). Lo que está oculto por
  // search no se toca — sería confuso para el usuario.
  const collapseAllVisible = useCallback(() => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      for (const d of visibleDetails) {
        next.add(d.idCargoDetail);
        localStorage.setItem(`DETAIL_HIDDEN_${d.idCargoDetail}`, 'true');
      }
      return next;
    });
  }, [visibleDetails]);

  const expandAllVisible = useCallback(() => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      for (const d of visibleDetails) {
        next.delete(d.idCargoDetail);
        localStorage.removeItem(`DETAIL_HIDDEN_${d.idCargoDetail}`);
      }
      return next;
    });
  }, [visibleDetails]);

  // Track de apertura de carga — solo una vez al montar.
  useEffect(() => {
    if (idCargo) track({ name: 'position_open', params: { id_cargo: Number(idCargo) } });
  }, [idCargo]);

  // Cuando se abre el modal de confirmación de eliminar, esperamos a que
  // el teclado virtual aparezca y reposicionamos el input para que quede
  // por encima de él. En el peor de los casos (teclado muy alto) el overlay
  // tiene overflow auto y el dialog se desplaza dentro del overlay.
  useEffect(() => {
    if (!deleteTarget) return;
    const t = setTimeout(() => {
      deleteInputRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 350);
    return () => clearTimeout(t);
  }, [deleteTarget]);

  // Cuando el SW procesa la cola y el contador global cambia, recargamos
  // para reflejar qué pendientes ya se subieron y cuáles siguen en espera.
  const pendingGlobal = usePendingCount();
  const lastPendingRef = useRef(pendingGlobal);
  useEffect(() => {
    if (pendingGlobal !== lastPendingRef.current) {
      lastPendingRef.current = pendingGlobal;
      fetchDetails();
    }
  }, [pendingGlobal, fetchDetails]);

  const pullState = usePullToRefresh(fetchDetails);

  const handleSave = async (
    idCargoDetail: number,
    fields: { netWeight: number; tare: number; packageNumber: number }
  ) => {
    setSavingId(idCargoDetail);
    const current = details.find((d) => d.idCargoDetail === idCargoDetail);
    if (!current) { setSavingId(null); return; }

    // ── Edición inline sobre un NEW pendiente (sin id de servidor) ──────
    // Al item le asignamos idCargoDetail = -pendingId al fusionar; aquí
    // recuperamos el id de cola y actualizamos el detalle directamente en
    // IndexedDB. Espejo de updateOffline en BBDDOfflineRepository (Android).
    if (idCargoDetail <= 0) {
      const pendingId = -idCargoDetail;
      try {
        await updatePendingDetailFields(pendingId, fields);
        notifyPendingChanged();
        setDetails((prev) => prev.map((d) =>
          d.idCargoDetail === idCargoDetail ? { ...d, ...fields } : d
        ));
        // Si hay red real y no estamos en modo offline manual, disparamos
        // sync; si no, queda en cola hasta que se restaure conectividad.
        if (!isOfflineForUser()) triggerOfflineFlush(true);
        setToast(isOfflineForUser() ? t('detail.toast.savedOffline') : t('detail.toast.savedSyncing'));
      } catch (e) {
        captureError(e, { where: 'DetailPage.handleSave.pendingNew', pendingId });
        setToast(t('detail.toast.saveError'));
      } finally {
        setSavingId(null);
      }
      return;
    }

    // Atajo offline: si no hay red O el usuario está en modo offline manual,
    // encolamos directamente. Las ediciones pendientes sobre el mismo
    // detalle se deduplican en addPendingDetail.
    if (isOfflineForUser() && idCargoDetail > 0) {
      try {
        await addPendingDetail(Number(idCargo), 'EDIT', { ...current, ...fields });
        notifyPendingChanged();
        setDetails((prev) => prev.map((d) =>
          d.idCargoDetail === idCargoDetail ? { ...d, ...fields, isOffline: true } : d
        ));
        setToast(t('detail.toast.savedOfflineWillSync'));
      } catch (e) {
        captureError(e, { where: 'DetailPage.handleSave.queueEdit', idCargoDetail });
        setToast(t('detail.toast.saveOfflineError'));
      } finally {
        setSavingId(null);
      }
      return;
    }

    try {
      await editDetail(idCargoDetail, { ...current, ...fields });
      track({ name: 'detail_edit_success', params: { online: true } });
      setDetails((prev) =>
        prev.map((d) => d.idCargoDetail === idCargoDetail ? { ...d, ...fields } : d)
      );
      // Espejo en la caché local para que offline vea los datos editados.
      await updateCachedCargoDetails(Number(idCargo), (list) =>
        list.map((d) => d.idCargoDetail === idCargoDetail ? { ...d, ...fields } : d)
      ).catch((e) => captureError(e, { where: 'DetailPage.handleSave.updateCache', idCargo, idCargoDetail }));
      invalidateSwApiCache({ idCargo: Number(idCargo), idCargoDetail });
      setToast(t('detail.toast.saved'));
    } catch (err: unknown) {
      // Solo encolamos si fue caída de red real (axios sin response y offline).
      const axiosErr = err as { isAxiosError?: boolean; response?: { status?: number } };
      const noResponse = !!axiosErr?.isAxiosError && axiosErr.response == null;
      if (noResponse && isOfflineForUser() && idCargoDetail > 0) {
        try {
          await addPendingDetail(Number(idCargo), 'EDIT', { ...current, ...fields });
          notifyPendingChanged();
          triggerOfflineFlush(true);
          setDetails((prev) => prev.map((d) =>
            d.idCargoDetail === idCargoDetail ? { ...d, ...fields, isOffline: true } : d
          ));
          setToast(t('detail.toast.savedOfflineWillSync'));
        } catch (e) {
          captureError(e, { where: 'DetailPage.handleSave.queueOnFailure', idCargoDetail });
          setToast(t('detail.toast.errorRetry'));
        }
      } else {
        captureError(err, { where: 'DetailPage.handleSave.editDetail', idCargoDetail });
        setToast(t('detail.toast.errorRetry'));
      }
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = (detail: Detail) => {
    // Abrimos el modal con input — el delete real se ejecuta al confirmar.
    setDeleteInput('');
    setDeleteTarget(detail);
  };

  const performDelete = async () => {
    const detail = deleteTarget;
    if (!detail) return;
    setDeletingNow(true);

    // ── Caso especial: eliminar un NEW pendiente que aún no se ha sincronizado.
    // En la lista esos items se muestran con idCargoDetail = -pendingId (mi
    // convención de display). No tienen registro en servidor, así que basta
    // con eliminar la entrada de la cola.
    if (detail.idCargoDetail <= 0) {
      const pendingId = -detail.idCargoDetail;
      try {
        await deletePendingDetail(pendingId);
        notifyPendingChanged();
        track({ name: 'detail_delete_success', params: { online: false } });
        setDetails((prev) => prev.filter((d) => d.idCargoDetail !== detail.idCargoDetail));
        setToast(t('detail.toast.deleted'));
      } catch (e) {
        captureError(e, { where: 'DetailPage.performDelete.pendingNew', pendingId });
        setToast(t('detail.toast.deleteError'));
      } finally {
        setDeletingNow(false);
        setDeleteTarget(null);
        setDeleteInput('');
      }
      return;
    }

    // Atajo offline: encolamos un DELETE para que el SW lo ejecute al volver
    // la red. Cubre tanto sin red real como modo offline manual.
    if (isOfflineForUser()) {
      try {
        await addPendingDetail(Number(idCargo), 'DELETE', detail);
        notifyPendingChanged();
        setDetails((prev) => prev.filter((d) => d.idCargoDetail !== detail.idCargoDetail));
        setToast(t('detail.toast.deletePending'));
      } catch (e) {
        captureError(e, { where: 'DetailPage.performDelete.queueOffline', idCargoDetail: detail.idCargoDetail });
        setToast(t('detail.toast.deleteOfflineError'));
      } finally {
        setDeletingNow(false);
        setDeleteTarget(null);
        setDeleteInput('');
      }
      return;
    }

    try {
      // El backend de DeleteCargoDetail NO borra las fotos asociadas; deja
      // huérfanos blobs en storage y filas en CargoDetailImages. Replicamos
      // el comportamiento de la app Android: listamos las fotos del detalle
      // y las borramos una a una vía DeleteImage antes del delete del detalle.
      try {
        const r = await getImages(detail.idCargoDetail);
        const data = r.data;
        const list = Array.isArray(data)
          ? (data as { imageName: string }[])
          : ((data as { listDataImage?: { imageName: string }[] })?.listDataImage ?? []);
        for (const img of list) {
          if (img.imageName) {
            try { await deleteImage(detail.idCargoDetail, img.imageName); } catch { /* foto huérfana, no bloqueamos */ }
          }
        }
      } catch { /* si falla listar fotos, seguimos con el delete del detalle */ }

      await deleteDetail(detail.idCargoDetail);
      track({ name: 'detail_delete_success', params: { online: true } });
      setDetails((prev) => prev.filter((d) => d.idCargoDetail !== detail.idCargoDetail));
      // Quitar también de la caché local para que la vista offline no
      // muestre el item como fantasma si el usuario entra en modo manual
      // o pierde cobertura antes del próximo fetch online.
      await updateCachedCargoDetails(Number(idCargo), (list) =>
        list.filter((d) => d.idCargoDetail !== detail.idCargoDetail)
      ).catch((e) => captureError(e, { where: 'DetailPage.performDelete.updateCache', idCargo, idCargoDetail: detail.idCargoDetail }));
      // Invalidar la caché HTTP del SW para esa carga y detalle, así un
      // fetch posterior en red mala no devuelve la versión vieja.
      invalidateSwApiCache({ idCargo: Number(idCargo), idCargoDetail: detail.idCargoDetail });
      setToast(t('detail.toast.deleted'));
    } catch (err: unknown) {
      // Si fue caída de red real, encolamos el DELETE para reintento.
      const axiosErr = err as { isAxiosError?: boolean; response?: { status?: number } };
      const noResponse = !!axiosErr?.isAxiosError && axiosErr.response == null;
      if (noResponse && isOfflineForUser()) {
        try {
          await addPendingDetail(Number(idCargo), 'DELETE', detail);
          notifyPendingChanged();
          triggerOfflineFlush(true);
          setDetails((prev) => prev.filter((d) => d.idCargoDetail !== detail.idCargoDetail));
          setToast(t('detail.toast.deletePending'));
        } catch (e) {
          captureError(e, { where: 'DetailPage.performDelete.queueOnFailure', idCargoDetail: detail.idCargoDetail });
          setToast(t('detail.toast.deleteError'));
        }
      } else {
        captureError(err, { where: 'DetailPage.performDelete.deleteDetail', idCargoDetail: detail.idCargoDetail });
        setToast(t('detail.toast.deleteError'));
      }
    } finally {
      setDeletingNow(false);
      setDeleteTarget(null);
      setDeleteInput('');
    }
  };

  const cancelDelete = () => {
    if (deletingNow) return;
    setDeleteTarget(null);
    setDeleteInput('');
  };

  // Palabra de confirmación localizada (es: "si", en: "yes"). Aceptamos también
  // "si"/"sí" siempre, por compatibilidad.
  const confirmWord = t('detail.deleteConfirmWord');
  const normalizedConfirm = deleteInput.trim().toLowerCase();
  const canConfirmDelete =
    normalizedConfirm === confirmWord.toLowerCase() || normalizedConfirm === 'si' || normalizedConfirm === 'sí';

  // La Function devuelve "OK REF, xxxx" en éxito y "Something went wrong, ..." en fallo.
  // Comparamos con OK al principio (case-insensitive) para no acoplarnos a un texto exacto.
  const isVgmSuccess = (msg: string) => /^\s*ok\b/i.test(msg);

  const persistVgmConfirmation = async (detail: Detail, responseMessage: string) => {
    if (!isVgmSuccess(responseMessage)) return false;
    try {
      await editCargoDetailVgmReference(detail.idCargoDetail, responseMessage.trim(), detail.containerNumber);
      return true;
    } catch (err) {
      console.warn('No se pudo guardar la confirmación VGM', err);
      return false;
    }
  };

  const handleVgm = async (detail: Detail) => {
    if (!confirm(t('detail.confirmSendVgm', { container: detail.containerNumber }))) return;
    try {
      const res = await sendVgmByDetail(detail.idCargoDetail);
      const message = typeof res.data === 'string' ? res.data : '';
      const ok = await persistVgmConfirmation(detail, message);
      track({ name: 'vgm_sent', params: { success: isVgmSuccess(message), batch: false } });
      setToast(message || t('detail.toast.vgmSent'));
      if (ok) await fetchDetails();
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: unknown }; message?: string };
      const status = e.response?.status ?? '';
      const body = e.response?.data;
      const msg = typeof body === 'string' ? body : JSON.stringify(body ?? e.message ?? err);
      alert(t('detail.vgmError', { status, msg }));
    }
  };

  const handleSendAllVgm = () => {
    const eligible = details.filter((d) => d.idCargoDetail > 0);
    if (eligible.length === 0) return;
    setVgmConfirmOpen(true);
  };

  const confirmSendAllVgm = async () => {
    if (!position) return;
    setVgmConfirmOpen(false);
    setSendingVgm(true);
    const eligible = details.filter((d) => d.idCargoDetail > 0);
    let successCount = 0;
    const errors: string[] = [];

    let confirmedCount = 0;
    for (const detail of eligible) {
      try {
        const res = await sendVgmByDetail(detail.idCargoDetail);
        const message = typeof res.data === 'string' ? res.data : '';
        const ok = isVgmSuccess(message);
        track({ name: 'vgm_sent', params: { success: ok, batch: true } });
        if (ok) {
          successCount++;
          if (await persistVgmConfirmation(detail, message)) confirmedCount++;
        } else {
          errors.push(`#${detail.idCargoDetail} (${detail.containerNumber}): ${message || 'respuesta no OK'}`);
        }
      } catch (err: unknown) {
        const e = err as { response?: { status?: number; data?: unknown }; message?: string };
        const status = e.response?.status ?? 'red';
        const body = e.response?.data;
        const msg = typeof body === 'string' ? body : JSON.stringify(body ?? e.message ?? err);
        errors.push(`#${detail.idCargoDetail} (${detail.containerNumber}) [${status}]: ${msg}`);
      }
    }
    if (confirmedCount > 0) await fetchDetails();

    setSendingVgm(false);
    const lines: string[] = [];
    if (successCount > 0) lines.push(t('detail.vgmBatchOk', { count: successCount }));
    if (errors.length > 0) {
      lines.push(t('detail.vgmBatchErrors', { count: errors.length }));
      errors.forEach((e) => lines.push(`• ${e}`));
    }
    if (lines.length > 0) alert(lines.join('\n'));
  };

  return (
    <div className="detail-page">
      <PullIndicator {...pullState} />
      {/* Self-managed: el proveedor sube al título de la cabecera, que estaba
          vacío porque estos usuarios no tienen booking. El clásico conserva
          exactamente el comportamiento anterior (supplierName llega nulo). */}
      <AppHeader
        showBack
        title={position?.supplierName || (position?.bookingPortic ?? t('detail.title'))}
        rightContent={
          <div className="header-actions">
            {vgmEnabled && (
              <button
                className="add-detail-btn"
                onClick={handleSendAllVgm}
                disabled={sendingVgm}
                aria-label={t('detail.sendAllVgmAria')}
              >
                {sendingVgm
                  ? <div className="spinner spinner--small spinner--white" />
                  : (
                    <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
                      <path d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/>
                    </svg>
                  )
                }
              </button>
            )}
            {prefs.addCargoEnabled && (
              <button
                className="add-detail-btn"
                onClick={() => navigate(`/edit-detail/${idCargo}/new`, { state: { position, date: dateStr } })}
                aria-label={t('detail.addDetail')}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                </svg>
              </button>
            )}
          </div>
        }
      />

      {position && (
        <div className="detail-position-info">
          {/* Barra de carga lineal superpuesta (absolute) para no
              añadir altura al layout entre el header y este banner */}
          <div className={`linear-progress ${loading ? 'linear-progress--active' : ''}`}>
            <div className="linear-progress__bar" />
          </div>
          {/* En self-managed: a la izquierda la DIRECCIÓN de la planning (el
              proveedor pasó al título de la cabecera) y a la derecha el producto
              real — factoryName viene vacío y productName es el placeholder.
              En el clásico ambos caen a los campos de siempre. */}
          <div className="detail-position-row">
            <span>{cargoSelfManaged ? (position.locationText ?? '') : position.factoryName}</span>
            <span>{displayProductNames(position) ?? <DraftProduct text={productDraftOf(position)} />}</span>
          </div>
          <div className="detail-position-row detail-position-row--sub">
            <span>{position.carrierName}</span>
            <span>{t('detail.containers', { loaded: position.totalContainerLoaded, total: position.totalContainers })}</span>
          </div>
          {position.sealVet && (
            <div className="detail-position-row detail-position-row--sub">
              <span>{t('detail.vetSeal', { value: position.sealVet })}</span>
            </div>
          )}
          {dateStr && (
            <div className="detail-position-date">
              {(() => {
                const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
                return m ? `${m[3]}/${m[2]}/${m[1]}` : dateStr;
              })()}
            </div>
          )}
        </div>
      )}

      <div className="detail-toolbar">
        {hasES && (
        <label className="switch-label">
          <span className="switch-label__text">{t('detail.showAll')}</span>
          <span className="switch">
            <input
              type="checkbox"
              checked={showLoaded}
              onChange={(e) => {
                setShowLoaded(e.target.checked);
                localStorage.setItem('DETAIL_SHOW_LOADED', String(e.target.checked));
                // Cambiar de opción dispara un nuevo fetch (showLoaded está en
                // las deps de fetchDetails). Vaciamos la lista y activamos
                // loading para que el spinner cubra la espera. El retardo CSS
                // del PageSpinner (~150 ms) hace que NO aparezca cuando la
                // lectura es instantánea desde IDB (modo offline), evitando un
                // parpadeo cuando no se trae nada del back.
                setDetails([]);
                setLoading(true);
              }}
            />
            <span className="switch__slider" />
          </span>
        </label>
        )}
        <div className="detail-toolbar-right">
          {/* Expandir todo / Colapsar todo: aplican sobre los detalles
              VISIBLES (los que pasan el filtro de búsqueda y el toggle
              "Ver todas"). Persistencia por id en localStorage — mismo
              formato que el toggle individual de cada card. */}
          <button
            className="sort-btn"
            onClick={expandAllVisible}
            disabled={visibleDetails.length === 0}
            aria-label={t('detail.expandAll')}
            title={t('detail.expandAll')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
              <rect x="3" y="3" width="18" height="18" rx="3" />
              <line x1="12" y1="8" x2="12" y2="16" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
          </button>
          <button
            className="sort-btn"
            onClick={collapseAllVisible}
            disabled={visibleDetails.length === 0}
            aria-label={t('detail.collapseAll')}
            title={t('detail.collapseAll')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
              <rect x="3" y="3" width="18" height="18" rx="3" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
          </button>
          <span className="sort-btn-divider" aria-hidden="true" />
          <button
            className="sort-btn"
            onClick={() => setSortAscending((prev) => {
              const next = !prev;
              localStorage.setItem('DETAIL_SORT_ASC', String(next));
              return next;
            })}
            aria-label={sortAscending ? t('detail.sortAsc') : t('detail.sortDesc')}
          >
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              width="20"
              height="20"
              style={{ transform: sortAscending ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.3s' }}
            >
              <path d="M3 18h6v-2H3v2zm0-5h9v-2H3v2zm0-7v2h12V6H3zm16 9.59V7h-2v11.59l-3.29-3.3-1.42 1.41L17 21l4.71-4.29-1.42-1.42L18 16.59z"/>
            </svg>
          </button>
          <button
            className={`sort-btn ${searchOpen ? 'sort-btn--active' : ''}`}
            onClick={toggleSearch}
            aria-label={searchOpen ? t('detail.closeSearch') : t('detail.searchAria')}
          >
            {searchOpen ? (
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
            )}
          </button>
          <button
            className={`sort-btn ${sendingReport ? 'sort-btn--busy' : ''}`}
            onClick={sendReport}
            disabled={sendingReport || isOfflineForUser() || visibleDetails.every((d) => !(d.idCargoDetail > 0))}
            aria-label={t('detail.report.button')}
            title={t('detail.report.button')}
          >
            {/* Sobre con flecha: "enviar por correo" */}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="M3 7l9 6 9-6" />
            </svg>
          </button>
          <span className="detail-count">
            {searchQuery ? t('detail.searchCount', { shown: visibleDetails.length, total: details.length }) : t('detail.records', { count: details.length })}
          </span>
        </div>
      </div>

      {/* Input de búsqueda — expandible */}
      <div className={`search-bar ${searchOpen ? 'search-bar--open' : ''}`}>
        <div className="search-bar__inner">
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" className="search-bar__icon">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
          </svg>
          <input
            ref={searchInputRef}
            type="text"
            className="search-bar__input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('detail.searchPlaceholder')}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            onKeyDown={(e) => { if (e.key === 'Escape') closeSearch(); }}
          />
          {searchQuery && (
            <button
              type="button"
              className="search-bar__clear"
              onClick={() => { setSearchQuery(''); searchInputRef.current?.focus(); }}
              aria-label={t('detail.clearSearch')}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="detail-list">
        {/* Spinner mientras se cargan los detalles del back. El retardo CSS
            del componente evita que aparezca cuando los datos vienen al
            instante de IDB (offline / caché), así no parpadea si no hay
            espera real de red. */}
        {loading && details.length === 0 && <PageSpinner />}

        {!loading && details.length === 0 && (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="#ccc" width="64" height="64">
              <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12z" />
            </svg>
            <p>{t('detail.empty')}</p>
          </div>
        )}

        {!loading && details.length > 0 && visibleDetails.length === 0 && (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="#ccc" width="56" height="56">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
            <p>{t('detail.noResults', { query: searchQuery })}</p>
          </div>
        )}

        {visibleDetails.map((d, i) => (
          <DetailCard
            key={d.idCargoDetail}
            detail={d}
            order={sortAscending ? i + 1 : visibleDetails.length - i}
            position={position}
            dateStr={dateStr}
            prefs={prefs}
            savingId={savingId}
            hidden={hiddenIds.has(d.idCargoDetail)}
            onSetHidden={(v) => setDetailHidden(d.idCargoDetail, v)}
            onSave={handleSave}
            onDelete={handleDelete}
            onVgm={handleVgm}
            canVgm={vgmEnabled && !cargoSelfManaged}
            canPlate={features.truckPlate && !cargoSelfManaged}
            showSupplierStamp={features.supplierStamp}
            selfManaged={cargoSelfManaged}
            deliveryNote={deliveryNotes.get(d.idCargoDetail)}
            canManageDeliveryNote={features.deliveryNote}
          />
        ))}
      </div>

      {toast && <Toast msg={toast} onDone={() => setToast('')} />}

      {vgmConfirmOpen && (
        <div className="confirm-overlay" onClick={() => setVgmConfirmOpen(false)}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-dialog__icon">
              <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                <path d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/>
              </svg>
            </div>
            <h3 className="confirm-dialog__title">{t('detail.vgmDialogTitle')}</h3>
            <p className="confirm-dialog__desc">
              {t('detail.vgmDialogDesc', { count: details.filter((d) => d.idCargoDetail > 0).length })}
            </p>
            <div className="confirm-dialog__actions">
              <button className="btn btn--secondary" onClick={() => setVgmConfirmOpen(false)}>
                {t('common.cancel')}
              </button>
              <button className="btn btn--primary" onClick={confirmSendAllVgm}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
                {t('detail.send')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmación de eliminar con input para evitar clicks accidentales */}
      {deleteTarget && (
        <div className="confirm-overlay" onClick={cancelDelete}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-dialog__icon" style={{ background: 'var(--danger-bg-light)', color: 'var(--danger-dark)' }}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
              </svg>
            </div>
            <h3 className="confirm-dialog__title">{t('detail.deleteDialogTitle')}</h3>
            <p className="confirm-dialog__desc">
              {t('detail.deleteDialogDesc', {
                container: deleteTarget.containerNumber || `#${deleteTarget.idCargoDetail}`,
                word: confirmWord,
              })}
            </p>
            <input
              type="text"
              ref={deleteInputRef}
              autoFocus
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
              placeholder={t('detail.deletePlaceholder', { word: confirmWord })}
              className="delete-confirm-input"
              disabled={deletingNow}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canConfirmDelete && !deletingNow) {
                  e.preventDefault();
                  performDelete();
                }
              }}
            />
            <div className="confirm-dialog__actions">
              <button className="btn btn--secondary" onClick={cancelDelete} disabled={deletingNow}>
                {t('common.cancel')}
              </button>
              <button
                className="btn btn--danger"
                onClick={performDelete}
                disabled={!canConfirmDelete || deletingNow}
              >
                {deletingNow ? t('detail.deleting') : t('detail.card.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
